import { skipToken } from "@reduxjs/toolkit/query";
import {
  isShopifyInactiveSubscriptionResponse,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { useMemo } from "react";

export type ShopifySubscriptionActionLockResult = {
  isLocked: boolean;
  lockReason: string | undefined;
};

export type EntityRemovabilityResult = {
  notRemovable: boolean;
  notRemovableReason: string | undefined;
};

/**
 * Blocks destructive Shopify-billed actions until the subscription is canceled
 * (typically after uninstalling the Jugl App from the Shopify store).
 */
export const useShopifySubscriptionActionLock = (
  entityId: string | undefined,
  lockedReason: string
): ShopifySubscriptionActionLockResult => {
  const { subscriptionV2Api } = useRestApiProvider();
  const { isAnonymous } = useAnonymousSession();
  const { entitiesList } = useEntityProvider();

  const entity = entitiesList.find((item) => item.id === entityId);
  const isShopifySourced = entity?.metadata?.source === "shopify";

  const { data } = subscriptionV2Api.useGetSubscriptionQuery(
    entityId && !isAnonymous && !isShopifySourced
      ? { entityId, params: { mode: "lite" } }
      : skipToken
  );

  const result = useMemo<ShopifySubscriptionActionLockResult>(() => {
    if (!data || !entityId) {
      return { isLocked: false, lockReason: undefined };
    }

    const isLocked =
      isShopifyInactiveSubscriptionResponse(data) ||
      (data.provider === "shopify" && data.status !== "canceled");

    if (!isLocked) {
      return { isLocked: false, lockReason: undefined };
    }

    return { isLocked: true, lockReason: lockedReason };
  }, [data, entityId, lockedReason]);

  return result;
};

/**
 * Resolves whether a workspace can be deleted or not.
 * If not, it also provides a reason.
 */
export const useEntityRemovability = (
  entityId: string | undefined
): EntityRemovabilityResult => {
  const { isLocked, lockReason } = useShopifySubscriptionActionLock(
    entityId,
    WORKSPACE_DELETE_LOCKED_REASON
  );

  return {
    notRemovable: isLocked,
    notRemovableReason: lockReason,
  };
};

const WORKSPACE_DELETE_LOCKED_REASON =
  "To delete the workspace, you need to uninstall the Jugl App from the Shopify store first ℹ️";
